// ResetPasswordPage.tsx — Handles the reset link from Supabase email
// Route: /reset-password (Supabase redirects here after the user clicks the email link)
// Also handles /forgot-password for the request form

import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

const S = {
  root:  { minHeight:'100vh', background:'#000', color:'#fff', fontFamily:'monospace', display:'flex', flexDirection:'column' as const, alignItems:'center', justifyContent:'center', padding:'24px' },
  card:  { width:'100%', maxWidth:'380px' },
  logo:  { color:'#32CD32', fontSize:'18px', fontWeight:700, letterSpacing:'4px', marginBottom:'4px', textAlign:'center' as const },
  sub:   { color:'#555', fontSize:'10px', letterSpacing:'2px', marginBottom:'32px', textAlign:'center' as const },
  label: { color:'#777', fontSize:'10px', letterSpacing:'1px', marginBottom:'6px', display:'block' },
  input: { background:'#000', border:'1px solid #333', color:'#fff', padding:'14px', fontFamily:'monospace', fontSize:'14px', width:'100%', boxSizing:'border-box' as const, outline:'none', marginBottom:'12px' },
  btn:   { background:'#32CD32', color:'#000', border:'none', padding:'16px', fontWeight:700, fontFamily:'monospace', fontSize:'13px', letterSpacing:'1px', width:'100%', cursor:'pointer' },
  err:   { color:'#cc3333', fontSize:'12px', marginBottom:'12px' },
  ok:    { color:'#32CD32', fontSize:'13px', marginBottom:'12px', lineHeight:'1.6' },
  back:  { color:'#555', fontSize:'11px', textAlign:'center' as const, marginTop:'16px' },
};

// ── Forgot Password (request form) ─────────────────────────────────────────────
function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!email.trim()) { setError('Enter your email address'); return; }
    setLoading(true); setError('');
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: 'https://app.blacktopsystem.com/reset-password',
    });
    if (err) setError(err.message);
    else setSent(true);
    setLoading(false);
  };

  if (sent) return (
    <div style={S.card}>
      <div style={S.logo}>BLACK TOP TMS</div>
      <div style={S.sub}>PASSWORD RESET</div>
      <div style={S.ok}>
        ✓ Reset link sent to {email}<br/>
        Check your inbox and click the link to set a new password.<br/>
        <span style={{ color:'#555' }}>If you don't see it, check your spam folder.</span>
      </div>
      <a href="/" style={{ ...S.btn, display:'block', textAlign:'center' as const, textDecoration:'none' }}>BACK TO LOGIN</a>
    </div>
  );

  return (
    <div style={S.card}>
      <div style={S.logo}>BLACK TOP TMS</div>
      <div style={S.sub}>FORGOT PASSWORD</div>
      <label style={S.label}>YOUR EMAIL ADDRESS</label>
      <input type="email" placeholder="you@yourcompany.com" style={S.input} value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} />
      {error && <div style={S.err}>{error}</div>}
      <button onClick={handleSend} disabled={loading} style={S.btn}>{loading ? 'SENDING...' : 'SEND RESET LINK'}</button>
      <div style={S.back}><a href="/" style={{ color:'#555', textDecoration:'none' }}>← Back to login</a></div>
    </div>
  );
}

// ── Set New Password (after clicking email link) ────────────────────────────────
function SetNewPasswordForm() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async () => {
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) setError(err.message);
    else setDone(true);
    setLoading(false);
  };

  if (done) return (
    <div style={S.card}>
      <div style={S.logo}>BLACK TOP TMS</div>
      <div style={S.sub}>PASSWORD RESET</div>
      <div style={S.ok}>✓ Password updated successfully.</div>
      <a href="/" style={{ ...S.btn, display:'block', textAlign:'center' as const, textDecoration:'none' }}>SIGN IN →</a>
    </div>
  );

  return (
    <div style={S.card}>
      <div style={S.logo}>BLACK TOP TMS</div>
      <div style={S.sub}>SET NEW PASSWORD</div>
      <label style={S.label}>NEW PASSWORD</label>
      <input type="password" placeholder="8+ characters" style={S.input} value={password} onChange={e => setPassword(e.target.value)} />
      <label style={S.label}>CONFIRM PASSWORD</label>
      <input type="password" placeholder="Confirm new password" style={{ ...S.input, marginBottom:'20px' }} value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleReset()} />
      {error && <div style={S.err}>{error}</div>}
      <button onClick={handleReset} disabled={loading} style={S.btn}>{loading ? 'UPDATING...' : 'SET NEW PASSWORD'}</button>
    </div>
  );
}

// ── Main — detect which state we're in ─────────────────────────────────────────
export default function ResetPasswordPage() {
  const [hasSession, setHasSession] = useState(false);
  const [checking, setChecking] = useState(true);
  const isForgot = window.location.pathname.startsWith('/forgot');

  useEffect(() => {
    if (isForgot) { setChecking(false); return; }
    // Supabase puts the recovery token in the URL hash — getSession picks it up automatically
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data.session);
      setChecking(false);
    });
    // Also listen for the auth state change that fires when the hash token is consumed
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setHasSession(true);
    });
    return () => listener.subscription.unsubscribe();
  }, [isForgot]);

  if (checking) return (
    <div style={S.root}>
      <div style={{ color:'#555', fontFamily:'monospace', fontSize:'12px' }}>Verifying reset link...</div>
    </div>
  );

  return (
    <div style={S.root}>
      {isForgot ? <ForgotPasswordForm /> : hasSession ? <SetNewPasswordForm /> : <ForgotPasswordForm />}
    </div>
  );
}
